import React from "react";
import { Link } from "react-router-dom";
import { Calculator, PackageSearch, Truck, FileSearch, FileCheck2, ShieldCheck, ChevronRight } from "lucide-react";

/**
 * Zipments widget catalog. Script tag is loaded once globally in /public/index.html.
 * Each entry produces a route under /tools/<slug>.
 */
export const TOOLS = [
  {
    slug: "duty-tax-calculator",
    label: "Duty & Tax Calculator",
    tagline: "Estimate landed cost for Canada or US imports in seconds.",
    icon: Calculator,
    embed: "duty-tax-quote",
    iframeTitle: "Zipments Duty & Tax Calculator",
  },
  {
    slug: "pars-tracker",
    label: "PARS Tracker",
    tagline: "Track Pre-Arrival Review System releases for shipments into Canada.",
    icon: PackageSearch,
    embed: "pars",
    iframeTitle: "Zipments PARS Tracker",
  },
  {
    slug: "paps-tracker",
    label: "PAPS Tracker",
    tagline: "Look up US-bound Pre-Arrival Processing System status in real time.",
    icon: Truck,
    embed: "paps",
    iframeTitle: "Zipments PAPS Tracker",
  },
  {
    slug: "hs-classification",
    label: "HS Tariff Classification",
    tagline: "Find the right Harmonized System code for any commodity you ship.",
    icon: FileSearch,
    embed: "hs",
    iframeTitle: "Zipments HS Code Lookup",
  },
  {
    slug: "cusma-certification",
    label: "CUSMA Certification",
    tagline: "Generate a compliant Canada-US-Mexico Agreement certificate of origin.",
    icon: FileCheck2,
    embed: "cusma-certification/create",
    iframeTitle: "Zipments CUSMA Certificate",
  },
  {
    slug: "carm-bond",
    label: "CARM Bond",
    tagline: "Purchase a CARM release-prior-to-payment bond for CBSA imports.",
    icon: ShieldCheck,
    embed: "bonds/carm/create",
    iframeTitle: "Zipments CARM Bond",
  },
];

export function ToolPage({ tool }) {
  const Icon = tool.icon;
  return (
    <div className="bg-white" data-testid={`tool-page-${tool.slug}`}>
      {/* Header */}
      <section className="bg-gradient-to-b from-[#0B3D6E] to-[#0a2f56] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <nav className="text-xs text-slate-300 mb-6 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/tools" className="hover:text-white">Tools</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white">{tool.label}</span>
          </nav>
          <div className="flex items-start gap-4">
            <div className="hidden md:flex h-12 w-12 items-center justify-center bg-[#2E7BC7] rounded-md shrink-0">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <div className="font-label text-xs text-[#2E7BC7]">Cross-Border Tools · Free</div>
              <h1 className="mt-1 font-display font-black text-3xl md:text-5xl leading-tight">{tool.label}</h1>
              <p className="mt-4 max-w-2xl text-slate-200">{tool.tagline}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Widget */}
      <section className="py-10 md:py-16 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="bg-[#0B3D6E] text-white px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-[#2E7BC7]" />
                <span className="font-display font-bold text-sm tracking-wide">{tool.label}</span>
              </div>
              <div className="text-[10px] font-label tracking-[0.15em] uppercase text-slate-300">Live · Powered by Zipments</div>
            </div>
            <iframe
              id="zipments-web-widget-iframe"
              src={`https://widgets.zipments.io/064d727dd7/${tool.embed}`}
              width="100%"
              height="900px"
              title={tool.iframeTitle}
              frameBorder="0"
              allow="clipboard-write; fullscreen; web-share"
              className="block w-full bg-white"
              data-testid={`tool-iframe-${tool.slug}`}
            />
          </div>
          <p className="mt-4 text-xs text-slate-500 max-w-3xl">
            Tool is provided for reference. For firm quotes, certificates, or bonds tied to a specific shipment, contact our cross-border desk.
          </p>
        </div>
      </section>

      {/* Related tools */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="font-label text-xs text-[#2E7BC7]">Other Tools</div>
          <h3 className="mt-2 font-display font-black text-2xl md:text-3xl text-[#0B3D6E]">More cross-border helpers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TOOLS.filter(t => t.slug !== tool.slug).slice(0, 3).map(t => {
              const RIcon = t.icon;
              return (
                <Link key={t.slug} to={`/tools/${t.slug}`}
                  className="group border border-gray-200 hover:border-[#2E7BC7] rounded-md p-5 transition-colors"
                  data-testid={`related-tool-${t.slug}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded flex items-center justify-center bg-[#0B3D6E]/5 text-[#2E7BC7] group-hover:bg-[#2E7BC7] group-hover:text-white transition-colors">
                      <RIcon className="h-4 w-4" />
                    </div>
                    <div className="font-display font-bold text-[#0B3D6E] group-hover:text-[#2E7BC7]">{t.label}</div>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">{t.tagline}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function ToolsIndex() {
  return (
    <div className="bg-white" data-testid="tools-index-page">
      <section className="bg-gradient-to-b from-[#0B3D6E] to-[#0a2f56] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="font-label text-xs text-[#2E7BC7]">Cross-Border Tools</div>
          <h1 className="mt-2 font-display font-black text-4xl md:text-6xl leading-tight">Free tools for cross-border shippers.</h1>
          <p className="mt-4 max-w-2xl text-slate-200 text-lg">
            Duty & tax estimates, PARS/PAPS tracking, HS classification, CUSMA certificates and CARM bonds — all in one place.
          </p>
        </div>
      </section>
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOOLS.map(t => {
            const Icon = t.icon;
            return (
              <Link key={t.slug} to={`/tools/${t.slug}`}
                className="group border border-gray-200 hover:border-[#2E7BC7] rounded-lg p-6 transition-all hover:shadow-md"
                data-testid={`tools-card-${t.slug}`}>
                <div className="h-11 w-11 rounded-md flex items-center justify-center bg-[#0B3D6E]/5 text-[#2E7BC7] group-hover:bg-[#0B3D6E] group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display font-bold text-lg text-[#0B3D6E] group-hover:text-[#2E7BC7]">{t.label}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.tagline}</p>
                <div className="mt-4 text-xs font-medium text-[#2E7BC7] inline-flex items-center gap-1">
                  Open tool <ChevronRight className="h-3 w-3" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
